import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  VStack,
  HStack,
  Button,
  useToast,
  Badge,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { FiSearch, FiRefreshCw, FiDownload } from 'react-icons/fi';
import { userActivityLogApi } from '../config/api';
import { useAuth } from '../context/AuthContext';
import DataTable from 'react-data-table-component';

const UserActivityLog = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState([]);
  const [topItems, setTopItems] = useState([]);
  const [filters, setFilters] = useState({
    activityType: '',
    pageName: '',
    itemType: '',
    startDate: '',
    endDate: '',
    limit: 100,
    offset: 0
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await userActivityLogApi.get('/list', {
        params
      });

      if (response.data && response.data.status === 200) {
        setLogs(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to fetch logs',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await userActivityLogApi.get('/statistics', {
        params: {
          ...params,
          groupBy: 'activity_type'
        }
      });

      if (response.data && response.data.status === 200) {
        setStatistics(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Fetch top items
  const fetchTopItems = async () => {
    try {
      const params = {};
      if (filters.itemType) params.itemType = filters.itemType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const response = await userActivityLogApi.get('/top-items', {
        params: {
          ...params,
          limit: 10
        }
      });

      if (response.data && response.data.status === 200) {
        setTopItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching top items:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStatistics();
    fetchTopItems();
  }, [filters]);

  // DataTable columns
  const columns = [
    {
      name: 'Time',
      selector: row => new Date(row.CREATED_TIME).toLocaleString('id-ID'),
      sortable: true,
      width: '180px'
    },
    {
      name: 'NIK',
      selector: row => row.NIK || '-',
      sortable: true,
      width: '120px'
    },
    {
      name: 'Activity Type',
      selector: row => row.ACTIVITY_TYPE,
      sortable: true,
      cell: row => (
        <Badge colorScheme={
          row.ACTIVITY_TYPE === 'page_view' ? 'blue' :
          row.ACTIVITY_TYPE === 'item_view' ? 'green' :
          row.ACTIVITY_TYPE === 'search' ? 'purple' :
          row.ACTIVITY_TYPE === 'filter' ? 'orange' :
          row.ACTIVITY_TYPE === 'wishlist_add' ? 'pink' :
          row.ACTIVITY_TYPE === 'wishlist_remove' ? 'red' :
          'gray'
        }>
          {row.ACTIVITY_TYPE}
        </Badge>
      ),
      width: '150px'
    },
    {
      name: 'Page',
      selector: row => row.PAGE_NAME || row.PAGE_PATH || '-',
      sortable: true,
      width: '150px'
    },
    {
      name: 'Item Type',
      selector: row => row.ITEM_TYPE || '-',
      sortable: true,
      width: '120px'
    },
    {
      name: 'Item Name',
      selector: row => row.ITEM_NAME || '-',
      sortable: true,
      wrap: true
    },
    {
      name: 'Action Detail',
      selector: row => {
        if (row.ACTION_DETAIL) {
          try {
            const detail = typeof row.ACTION_DETAIL === 'string' 
              ? JSON.parse(row.ACTION_DETAIL) 
              : row.ACTION_DETAIL;
            return JSON.stringify(detail);
          } catch {
            return row.ACTION_DETAIL;
          }
        }
        return '-';
      },
      wrap: true,
      width: '200px'
    },
    {
      name: 'Device',
      selector: row => `${row.DEVICE_TYPE || '-'} / ${row.BROWSER || '-'}`,
      width: '150px'
    }
  ];

  // Filtered logs
  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;
    const searchLower = searchTerm.toLowerCase();
    return logs.filter(log => 
      (log.NIK && log.NIK.toLowerCase().includes(searchLower)) ||
      (log.PAGE_NAME && log.PAGE_NAME.toLowerCase().includes(searchLower)) ||
      (log.ITEM_NAME && log.ITEM_NAME.toLowerCase().includes(searchLower)) ||
      (log.ACTIVITY_TYPE && log.ACTIVITY_TYPE.toLowerCase().includes(searchLower))
    );
  }, [logs, searchTerm]);

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">User Activity Log Report</Heading>
        
        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          {statistics.map((stat, index) => (
            <Card key={index}>
              <CardBody>
                <Stat>
                  <StatLabel>{stat.group_value || 'Unknown'}</StatLabel>
                  <StatNumber>{stat.count || 0}</StatNumber>
                  <StatHelpText>Total Activities</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        {/* Filters */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} wrap="wrap">
                <Select
                  placeholder="All Activity Types"
                  value={filters.activityType}
                  onChange={(e) => setFilters({ ...filters, activityType: e.target.value, offset: 0 })}
                  maxW="200px"
                >
                  <option value="page_view">Page View</option>
                  <option value="item_view">Item View</option>
                  <option value="search">Search</option>
                  <option value="filter">Filter</option>
                  <option value="click">Click</option>
                  <option value="image_view">Image View</option>
                  <option value="wishlist_add">Wishlist Add</option>
                  <option value="wishlist_remove">Wishlist Remove</option>
                  <option value="booking_click">Booking Click</option>
                  <option value="contact_click">Contact Click</option>
                </Select>

                <Select
                  placeholder="All Pages"
                  value={filters.pageName}
                  onChange={(e) => setFilters({ ...filters, pageName: e.target.value, offset: 0 })}
                  maxW="200px"
                >
                  <option value="Home">Home</option>
                  <option value="Hotel">Hotel</option>
                  <option value="Concierge">Concierge</option>
                  <option value="Lounge">Lounge</option>
                  <option value="Baggage">Baggage</option>
                  <option value="Wishlist">Wishlist</option>
                  <option value="Retail">Retail</option>
                  <option value="Attraction">Attraction</option>
                </Select>

                <Select
                  placeholder="All Item Types"
                  value={filters.itemType}
                  onChange={(e) => setFilters({ ...filters, itemType: e.target.value, offset: 0 })}
                  maxW="200px"
                >
                  <option value="hotel">Hotel</option>
                  <option value="concierge">Concierge</option>
                  <option value="lounge">Lounge</option>
                  <option value="baggage">Baggage</option>
                  <option value="retail">Retail</option>
                  <option value="attraction">Attraction</option>
                </Select>

                <Input
                  type="date"
                  placeholder="Start Date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value, offset: 0 })}
                  maxW="200px"
                />

                <Input
                  type="date"
                  placeholder="End Date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value, offset: 0 })}
                  maxW="200px"
                />

                <Button
                  leftIcon={<FiRefreshCw />}
                  onClick={() => {
                    setFilters({
                      activityType: '',
                      pageName: '',
                      itemType: '',
                      startDate: '',
                      endDate: '',
                      limit: 100,
                      offset: 0
                    });
                    setSearchTerm('');
                  }}
                >
                  Reset
                </Button>
              </HStack>

              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch />
                </InputLeftElement>
                <Input
                  placeholder="Search by NIK, Page, Item Name, or Activity Type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </VStack>
          </CardBody>
        </Card>

        {/* Top Items */}
        {topItems.length > 0 && (
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>Top Items Accessed</Heading>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Item Type</Th>
                      <Th>Item Name</Th>
                      <Th>View Count</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topItems.map((item, index) => (
                      <Tr key={index}>
                        <Td><Badge>{item.ITEM_TYPE}</Badge></Td>
                        <Td>{item.ITEM_NAME}</Td>
                        <Td><Badge colorScheme="green">{item.view_count}</Badge></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        )}

        {/* Logs Table */}
        <Card>
          <CardBody>
            <HStack justify="space-between" mb={4}>
              <Heading size="md">Activity Logs</Heading>
              <Text fontSize="sm" color="gray.500">
                Total: {filteredLogs.length} logs
              </Text>
            </HStack>
            
            {loading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="xl" />
              </Box>
            ) : (
              <DataTable
                columns={columns}
                data={filteredLogs}
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                highlightOnHover
                pointerOnHover
                striped
                dense
              />
            )}
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default UserActivityLog;

